import { Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAppStore } from "@/stores";
import useSWR from "swr";
import { Loader } from "@/components/Loader";
import { sortByStringKey } from "@revelate/utils";
import { TableType, get, getAll, updateCompanyCurrency } from "@/lib/supabase";
import { NestedCurrency } from "@revelate/types";
import { format } from "date-fns";
import {
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
  Tooltip,
} from "@/components/ui/tooltip";
import { getExchangeRateToDefaultCurrency, isDefaultCurrency } from "@/lib/app";
import { ExchangeRatesForm } from "./components/exchange-rates-form";
import { ErrorWithRetry } from "@/components/ErrorWithRetry";

export function CurrenciesPage() {
  const currentUser = useAppStore((state) => state.currentUser);
  const {
    data: company,
    error: companyError,
    isLoading: companyIsLoading,
    mutate: mutateCompany,
  } = useSWR(
    [
      "companies" as TableType,
      currentUser ? currentUser?.company_id?.toString() : null,
    ],
    ([table, companyId]) => get(table, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
  const {
    data: currencies,
    error,
    isLoading,
    mutate,
  } = useSWR(
    ["currencies" as TableType, company ? company?.id.toString() : null],
    ([table, companyId]) => getAll(table, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const updateCurrency = async (currencyId: number) => {
    if (!company) return;
    const newCompany = await updateCompanyCurrency(company.id, currencyId);
    if (newCompany) {
      mutateCompany(newCompany);
    }
  };

  if (isLoading || companyIsLoading || !currencies || !company) {
    return <Loader />;
  }
  if (error || companyError) {
    return (
      <ErrorWithRetry
        error={error.message || companyError.message}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <main className="flex-1 sm:py-0">
      <Card x-chunk="dashboard-05-chunk-3" className="border-transparent">
        <div className="pt-3 pb-8">
          <CardHeader className="flex p-0">
            <CardTitle>
              <h1>
                Currencies
              </h1>
            </CardTitle>
          </CardHeader>
        </div>
        <CardContent className="px-0">
          {currencies && currencies.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Currency Code</TableHead>

                  <TableHead className="hidden sm:table-cell">
                    Date added
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    Exchange rate to {company?.default_currency?.code}
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    Valid until
                  </TableHead>

                  <TableHead className="text-right"></TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(currencies as NestedCurrency[])
                  ?.sort(sortByStringKey("code"))
                  .map((currency, index) => {
                    const exchangeRate = getExchangeRateToDefaultCurrency(
                      currency,
                      company.default_currency
                    );
                    const defaultCurrency = isDefaultCurrency(
                      company,
                      currency.id
                    );
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <div className="flex flex-row items-center gap-2">
                            <p className="font-medium">
                              {currency.code}
                            </p>
                            {isDefaultCurrency(company, currency.id) && (
                              <Badge variant="approved">
                                Default
                              </Badge>
                            )}
                          </div>
                          <div className="hidden text-xs text-muted-foreground md:inline">
                            {defaultCurrency
                              ? "Company Currency"
                              : "Deal Currency"}
                          </div>
                        </TableCell>

                        <TableCell className="hidden sm:table-cell">
                          {format(
                            new Date(currency.created_at),
                            "MMM dd, yyyy"
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {!defaultCurrency && (
                            <Badge
                              variant={
                                exchangeRate ? "paid" : "destructive"
                              }
                            >
                              {exchangeRate
                                ? `1 ${currency.code} = ${exchangeRate?.rate} ${company?.default_currency?.code}`
                                : "Missing"}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {" "}
                          {exchangeRate && exchangeRate.valid_to
                            ? format(
                              new Date(exchangeRate?.valid_to || ""),
                              "MMM dd, yyyy"
                            )
                            : ""}
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-3">
                            <ExchangeRatesForm
                              company={company}
                              currency={currency}
                              exchangeRate={exchangeRate}
                              mutate={mutate}
                            />
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipContent>
                                  Set {currency.code} as default currency
                                </TooltipContent>
                                <TooltipTrigger asChild>
                                  {!defaultCurrency && (
                                    <Button
                                      onClick={() =>
                                        updateCurrency(currency.id)
                                      }
                                      variant="outline"
                                      size="icon"
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                  )}
                                </TooltipTrigger>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          ) : (
            <p className="text-lg text-muted-foreground text-center">
              Sync deals to auto-populate currencies 💸
            </p>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
